import { useRef, useState } from 'react';
import JSZip from 'jszip';
import { Download, X, FileArchive, FileCheck2, AlertTriangle } from 'lucide-react';
import { useVault } from '../../../core/lib/vault';
import { useCanAdminister } from '../lib/usePermissions';
import { api } from '../lib/api';
import { decryptJson, decryptBlob, fromB64 } from '../../../core/crypto';
import { localStore } from '../db';
import { pullAll, type CustomerPlain, type DocumentPlain } from '../sync';

/**
 * Manifest shape written to `manifest.json` inside the ZIP.
 *
 * Version history:
 * - v1: original shape, no per-file hashes, no counts (unverifiable).
 * - v2: per-item SHA-256 + explicit counts so a post-hoc "verify archive"
 *   pass can detect truncation or corruption.
 * - v3: adds a server-signed `provenance` block (vaultId, exportedBy,
 *   serverVersion, serverTimestamp, auditLogId) stamped by
 *   /api/vault/export-audit so each archive can be cross-referenced
 *   against the authoritative audit log. The verifier still accepts v2
 *   archives (treating them as un-provenanced); v1 is unverifiable.
 */
interface ExportManifest {
  exportedAt: string;
  format: 3;
  provenance: ExportProvenance;
  counts: {
    customersExpected: number;
    customersWritten: number;
    documentsExpected: number;
    documentsWritten: number;
    attachmentsExpected: number;
    attachmentsWritten: number;
  };
  customers: ManifestCustomer[];
  documents: ManifestDoc[];
  attachments: ManifestAttachment[];
}

interface ExportProvenance {
  vaultId: string;
  exportedBy: { userId: string; username: string };
  serverVersion: string;
  serverTimestamp: string;
  auditLogId: string;
}

interface ManifestCustomer {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
  updatedAt: string;
}

interface ManifestDoc {
  id: string;
  customerId: string;
  parentId: string | null;
  title: string;
  path: string;
  sha256: string;
  createdAt: string;
  updatedAt: string;
}

interface ManifestAttachment {
  id: string;
  customerId: string;
  /**
   * The document this attachment is still linked to. Null when the
   * original document is no longer part of the archive's scope (e.g.
   * it was soft-deleted while the attachment remained live). Callers
   * that need the historical pointer can read `orphanedFromDocumentId`.
   */
  documentId: string | null;
  /** Set only when `documentId` was nulled because the target doc isn't in scope. */
  orphanedFromDocumentId?: string;
  filename: string;
  mimetype: string;
  size: number;
  path: string;
  sha256: string;
}

interface ExportFailure {
  kind: 'customer' | 'document' | 'attachment-metadata' | 'attachment-blob';
  id: string;
  reason: string;
}

interface VerifyOutcome {
  ok: boolean;
  summary: string;
  issues: string[];
}

export function ExportVaultModal({ onClose }: { onClose: () => void }) {
  const vaultKey = useVault((s) => s.vaultKey);
  const canAdminister = useCanAdminister();
  const [includeAttachments, setIncludeAttachments] = useState(true);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [failures, setFailures] = useState<ExportFailure[]>([]);
  const [verifyOutcome, setVerifyOutcome] = useState<VerifyOutcome | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Defensive admin guard. The Workspace overflow menu already hides the
  // Export entry for non-admins, but `POST /api/vault/export-audit` is
  // server-gated — rendering the modal for a non-admin would just 403 the
  // moment they clicked Export. Short-circuit to null so that never happens.
  if (!canAdminister) return null;

  async function run() {
    if (!vaultKey) return;
    setBusy(true);
    setError(null);
    setFailures([]);
    setVerifyOutcome(null);

    try {
      // (A1) Fresh-pull before reading IndexedDB. Without this the export
      // reflects the last sync, not current server state — a silent
      // truncation hazard during disaster recovery.
      setProgress('Syncing latest changes from server…');
      await pullAll();

      setProgress('Collecting vault contents…');
      const zip = new JSZip();
      const localFailures: ExportFailure[] = [];

      const customersRaw = await localStore.listCustomers();
      const documentsRaw = await localStore.listDocuments();
      const customersLive = customersRaw.filter((c) => !c.deletedAt);
      const documentsLive = documentsRaw.filter((d) => !d.deletedAt);

      // Scope set: anything whose parent customer isn't live is considered
      // orphanage data and silently excluded from the archive (per the
      // "current state, not full dump" spec). Before the 0016 migration the
      // server used to leave attachments behind when a customer was
      // soft-deleted; filtering here is the belt-and-braces that kept the
      // old state from aborting the export with "parent customer missing".
      const liveCustomerIds = new Set(customersLive.map((c) => c.id));

      const manifestCustomers: ManifestCustomer[] = [];
      const manifestDocs: ManifestDoc[] = [];
      const manifestAttachments: ManifestAttachment[] = [];

      const customerSlugs = new Map<string, string>();
      const docSlugs = new Map<string, string>();

      for (const c of customersLive) {
        try {
          const plain = await decryptJson<CustomerPlain>(c.data.payload, vaultKey);
          const slug = uniqueSlug(plain.name || c.id, new Set(customerSlugs.values()));
          customerSlugs.set(c.id, slug);
          manifestCustomers.push({
            id: c.id,
            name: plain.name,
            slug,
            createdAt: c.createdAt,
            updatedAt: c.updatedAt,
          });
        } catch (err) {
          // (A2) Surface — no more silent `catch { /* skip */ }`.
          localFailures.push({
            kind: 'customer',
            id: c.id,
            reason: err instanceof Error ? err.message : 'decryption failed',
          });
        }
      }

      const docById = new Map<string, (typeof documentsLive)[number]>();
      for (const d of documentsLive) docById.set(d.id, d);

      // Scope: documents whose parent customer is live. Orphaned rows
      // (e.g. pre-0016 orphanage debris) are not part of the export.
      const documentsInScope = documentsLive.filter((d) =>
        liveCustomerIds.has(d.data.customerId),
      );

      const decryptedDocs = new Map<string, DocumentPlain>();
      for (const d of documentsInScope) {
        // Customer is in-scope but failed to decrypt — skip without a
        // per-document failure. The customer failure is already recorded
        // and will abort the export; piling on derivative document
        // failures just adds noise to the user's error list.
        if (!customerSlugs.has(d.data.customerId)) continue;
        try {
          const plain = await decryptJson<DocumentPlain>(d.data.payload, vaultKey);
          decryptedDocs.set(d.id, plain);
        } catch (err) {
          localFailures.push({
            kind: 'document',
            id: d.id,
            reason: err instanceof Error ? err.message : 'decryption failed',
          });
        }
      }

      const slugsUsedPerCustomer = new Map<string, Set<string>>();
      function slugFor(customerId: string, base: string): string {
        let set = slugsUsedPerCustomer.get(customerId);
        if (!set) {
          set = new Set();
          slugsUsedPerCustomer.set(customerId, set);
        }
        const s = uniqueSlug(base, set);
        set.add(s);
        return s;
      }

      setProgress('Collecting attachment names…');
      // (C4) Pagination: iterate every page so a large vault doesn't silently
      // truncate at the server's per-page cap.
      const attListRaw = await collectAllAttachments();
      // Scope: attachments whose parent customer is live. Pre-0016 the
      // server could hand back blobs whose customer was soft-deleted (the
      // cascade didn't reach them) — those are orphanage items and are
      // excluded from the recovery archive.
      const attList = attListRaw.filter((a) => liveCustomerIds.has(a.customerId));
      const attachmentFilenameById = new Map<string, string>();
      const attachmentMetaById = new Map<string, { filename: string; mimetype: string }>();
      for (const att of attList) {
        try {
          const meta = await decryptJson<{ filename?: string; mimetype?: string }>(
            att.metadata,
            vaultKey,
          );
          const filename = meta.filename || `attachment-${att.id}`;
          const mimetype = meta.mimetype || 'application/octet-stream';
          attachmentFilenameById.set(att.id, filename);
          attachmentMetaById.set(att.id, { filename, mimetype });
        } catch (err) {
          localFailures.push({
            kind: 'attachment-metadata',
            id: att.id,
            reason: err instanceof Error ? err.message : 'metadata decrypt failed',
          });
        }
      }

      setProgress('Writing pages…');
      for (const [docId, plain] of decryptedDocs) {
        const d = docById.get(docId)!;
        const customerSlug = customerSlugs.get(d.data.customerId)!;
        const title = plain.title || docId;

        let relPath: string;
        if (d.data.parentId && decryptedDocs.has(d.data.parentId)) {
          const parentPlain = decryptedDocs.get(d.data.parentId)!;
          const parentSlug =
            docSlugs.get(d.data.parentId) ??
            slugFor(d.data.customerId, parentPlain.title || d.data.parentId);
          docSlugs.set(d.data.parentId, parentSlug);
          const tabSlug = slugify(title);
          relPath = `${customerSlug}/${parentSlug}/${tabSlug || docId}.md`;
        } else {
          const pageSlug = docSlugs.get(docId) ?? slugFor(d.data.customerId, title);
          docSlugs.set(docId, pageSlug);
          relPath = `${customerSlug}/${pageSlug}.md`;
        }

        const md = markdownFor(plain, attachmentFilenameById);
        const mdBytes = new TextEncoder().encode(md);
        zip.file(relPath, mdBytes);
        manifestDocs.push({
          id: docId,
          customerId: d.data.customerId,
          parentId: d.data.parentId,
          title,
          path: relPath,
          sha256: await sha256Hex(mdBytes),
          createdAt: d.createdAt,
          updatedAt: d.updatedAt,
        });
      }

      // Set of in-scope document ids — used to decide whether an attachment's
      // `documentId` pointer is still meaningful. Attachments whose original
      // doc is gone (hard-deleted or soft-deleted-then-dropped) get the
      // pointer nulled in the manifest; the original id is preserved under
      // `orphanedFromDocumentId` for post-hoc traceability.
      const docIdsInScope = new Set(documentsInScope.map((d) => d.id));

      let totalAttachmentBytes = 0;
      if (includeAttachments) {
        setProgress('Downloading attachments…');
        for (const att of attList) {
          const customerSlug = customerSlugs.get(att.customerId);
          if (!customerSlug) {
            // In-scope (live customer) but the customer decryption itself
            // failed earlier — already recorded as a customer failure,
            // which will abort the export. Skip silently to avoid noise.
            continue;
          }
          const meta = attachmentMetaById.get(att.id);
          if (!meta) {
            // Metadata decrypt already failed and was recorded above.
            continue;
          }
          try {
            const fetched = await api.getAttachment(att.id);
            const plaintext = await decryptBlob(
              fromB64(fetched.iv),
              fetched.blob,
              vaultKey,
            );
            const relPath = `${customerSlug}/attachments/${meta.filename}`;
            zip.file(relPath, plaintext);
            const docPointerLive =
              att.documentId != null && docIdsInScope.has(att.documentId);
            manifestAttachments.push({
              id: att.id,
              customerId: att.customerId,
              documentId: docPointerLive ? att.documentId : null,
              ...(att.documentId && !docPointerLive
                ? { orphanedFromDocumentId: att.documentId }
                : {}),
              filename: meta.filename,
              mimetype: meta.mimetype,
              size: att.size,
              path: relPath,
              sha256: await sha256Hex(plaintext),
            });
            totalAttachmentBytes += plaintext.byteLength;
          } catch (err) {
            localFailures.push({
              kind: 'attachment-blob',
              id: att.id,
              reason: err instanceof Error ? err.message : 'blob fetch/decrypt failed',
            });
          }
        }
      }

      // (A3) Completeness gate: refuse to finalise a silently-truncated ZIP.
      // If any item failed, the user sees the list and can retry — better
      // than shipping a "recovery" archive that's missing rows.
      // Expected counts are scoped: documents/attachments whose parent
      // customer is soft-deleted aren't part of the recovery archive and
      // don't belong in the completeness gate. `attList` is already the
      // scoped list (raw pagination was filtered above).
      const counts = {
        customersExpected: customersLive.length,
        customersWritten: manifestCustomers.length,
        documentsExpected: documentsInScope.length,
        documentsWritten: manifestDocs.length,
        attachmentsExpected: includeAttachments ? attList.length : 0,
        attachmentsWritten: manifestAttachments.length,
      };
      const countMismatch =
        counts.customersWritten !== counts.customersExpected ||
        counts.documentsWritten !== counts.documentsExpected ||
        counts.attachmentsWritten !== counts.attachmentsExpected;
      if (localFailures.length > 0 || countMismatch) {
        setFailures(localFailures);
        setError(
          `Export aborted: ${localFailures.length} item(s) could not be exported. The ZIP was not created.`,
        );
        return;
      }

      // (A4) Audit-log the export BEFORE we hand the ZIP to the user. If the
      // audit call fails we still refuse to finalise — a compliance-gap'd
      // export is worse than a failed export. The server's response carries
      // the provenance block we bake into the manifest, so the archive is
      // self-describing about which server + user + audit row produced it.
      setProgress('Recording audit entry…');
      let provenance: ExportProvenance;
      try {
        const auditResult = await api.recordExportAudit({
          itemCounts: {
            customers: counts.customersWritten,
            documents: counts.documentsWritten,
            attachments: counts.attachmentsWritten,
          },
          includeAttachments,
          totalBytes: totalAttachmentBytes,
        });
        provenance = {
          vaultId: auditResult.vaultId,
          exportedBy: auditResult.exportedBy,
          serverVersion: auditResult.serverVersion,
          serverTimestamp: auditResult.serverTimestamp,
          auditLogId: auditResult.auditLogId,
        };
      } catch (err) {
        setError(
          'Export aborted: failed to record the audit entry on the server. ' +
            (err instanceof Error ? err.message : String(err)),
        );
        return;
      }

      const manifest: ExportManifest = {
        exportedAt: new Date().toISOString(),
        format: 3,
        provenance,
        counts,
        customers: manifestCustomers,
        documents: manifestDocs,
        attachments: manifestAttachments,
      };
      zip.file('manifest.json', JSON.stringify(manifest, null, 2));

      setProgress('Packing zip…');
      const blob = await zip.generateAsync({ type: 'blob' });
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      triggerDownload(blob, `documentation-export-${stamp}.zip`);
      setProgress('Done.');
      setTimeout(onClose, 600);
    } catch (err: any) {
      // Prefer a server-structured reason, then the Error.message, then a
      // stringified fallback for oddities (thrown primitives, bare network
      // aborts with empty message). Generic "Export failed." is last so we
      // always surface something concrete when the error carries info.
      const extracted =
        err?.body?.error ?? err?.message ?? (err != null ? String(err) : null);
      setError(extracted && extracted !== '[object Object]' ? extracted : 'Export failed.');
    } finally {
      setBusy(false);
    }
  }

  async function verifyPicked(file: File) {
    setBusy(true);
    setError(null);
    setVerifyOutcome(null);
    try {
      const zip = await JSZip.loadAsync(file);
      const manifestFile = zip.file('manifest.json');
      if (!manifestFile) {
        setVerifyOutcome({
          ok: false,
          summary: 'No manifest.json in archive — cannot verify.',
          issues: [],
        });
        return;
      }
      const text = await manifestFile.async('string');
      // Typed loosely because this is parsing a v1/v2/v3 manifest that
      // some older export produced; the format check below narrows.
      const manifest = JSON.parse(text) as Omit<Partial<ExportManifest>, 'format'> & {
        format?: number;
      };
      // v2 carries per-file SHA-256s; v3 adds provenance; v4 (scheduled
      // Dropbox backups) adds a `backup` block + `dropboxContentHash` per
      // file but is otherwise identical from the verifier's perspective —
      // the per-file SHA-256s are still the source of truth for local
      // verification. Anything older (v1 or missing) can't be verified
      // beyond presence.
      if (manifest.format !== 2 && manifest.format !== 3 && manifest.format !== 4) {
        setVerifyOutcome({
          ok: false,
          summary:
            'Manifest format is older than v2 — checksums not recorded. File presence was not verified.',
          issues: [],
        });
        return;
      }
      const issues: string[] = [];
      let docsOk = 0;
      for (const d of manifest.documents ?? []) {
        const entry = zip.file(d.path);
        if (!entry) {
          issues.push(`missing file: ${d.path}`);
          continue;
        }
        const bytes = new Uint8Array(await entry.async('uint8array'));
        const sha = await sha256Hex(bytes);
        if (sha !== d.sha256) {
          issues.push(`sha mismatch: ${d.path}`);
        } else {
          docsOk++;
        }
      }
      let attOk = 0;
      for (const a of manifest.attachments ?? []) {
        const entry = zip.file(a.path);
        if (!entry) {
          issues.push(`missing file: ${a.path}`);
          continue;
        }
        const bytes = new Uint8Array(await entry.async('uint8array'));
        const sha = await sha256Hex(bytes);
        if (sha !== a.sha256) {
          issues.push(`sha mismatch: ${a.path}`);
        } else {
          attOk++;
        }
      }
      const expectedDocs = manifest.counts?.documentsWritten ?? (manifest.documents?.length ?? 0);
      const expectedAtts =
        manifest.counts?.attachmentsWritten ?? (manifest.attachments?.length ?? 0);
      if (docsOk !== expectedDocs) issues.push(`documents: ${docsOk}/${expectedDocs} verified`);
      if (attOk !== expectedAtts) issues.push(`attachments: ${attOk}/${expectedAtts} verified`);
      // Provenance is informational; verifier surfaces it so the user can
      // eyeball who/when/which-server, but absence only downgrades the OK
      // message (v2 archives are still fully checksum-verifiable).
      const prov = manifest.provenance;
      const okMessage =
        `OK: ${docsOk} documents + ${attOk} attachments match their checksums.` +
        (prov
          ? ` Exported by ${prov.exportedBy.username} on ${prov.serverTimestamp} (server ${prov.serverVersion}, audit ${prov.auditLogId}).`
          : ' (v2 manifest — no server-stamped provenance.)');
      setVerifyOutcome({
        ok: issues.length === 0,
        summary: issues.length === 0 ? okMessage : `Problems found: ${issues.length}.`,
        issues,
      });
    } catch (err: any) {
      setVerifyOutcome({
        ok: false,
        summary: err?.message ?? 'Could not read archive.',
        issues: [],
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
      <div className="card w-full max-w-md">
        <div className="flex items-center gap-2 border-b border-ink-800 px-4 py-3">
          <FileArchive size={18} className="text-dragon-500" />
          <h2 className="font-semibold">Export documentation</h2>
          <button onClick={onClose} className="btn-ghost ml-auto !p-1" disabled={busy}>
            <X size={16} />
          </button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <p className="text-ink-300">
            Produces a ZIP containing one Markdown file per page (grouped by customer) plus a
            <code className="mx-1 text-ink-400">manifest.json</code> with per-file SHA-256
            checksums. All content is decrypted client-side before packing; the server logs
            that an export occurred but never sees the plaintext.
          </p>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={includeAttachments}
              onChange={(e) => setIncludeAttachments(e.target.checked)}
              disabled={busy}
            />
            Include attachments
          </label>
          {progress && <p className="text-xs text-ink-500">{progress}</p>}
          {error && (
            <p className="text-xs text-red-400 flex items-start gap-1">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </p>
          )}
          {failures.length > 0 && (
            <div className="text-xs text-red-400 max-h-40 overflow-auto rounded border border-red-900/60 bg-red-950/40 p-2 space-y-0.5">
              {failures.slice(0, 20).map((f, i) => (
                <div key={i}>
                  <code>{f.kind}</code>:{' '}
                  <code>{f.id}</code> — {f.reason}
                </div>
              ))}
              {failures.length > 20 && <div>…and {failures.length - 20} more.</div>}
            </div>
          )}
          {verifyOutcome && (
            <div
              className={`text-xs rounded border p-2 space-y-0.5 max-h-40 overflow-auto ${
                verifyOutcome.ok
                  ? 'border-emerald-900/60 bg-emerald-950/40 text-emerald-300'
                  : 'border-red-900/60 bg-red-950/40 text-red-300'
              }`}
            >
              <div className="flex items-start gap-1">
                {verifyOutcome.ok ? (
                  <FileCheck2 size={14} className="mt-0.5 shrink-0" />
                ) : (
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                )}
                <span>{verifyOutcome.summary}</span>
              </div>
              {verifyOutcome.issues.slice(0, 20).map((issue, i) => (
                <div key={i} className="pl-5">
                  {issue}
                </div>
              ))}
              {verifyOutcome.issues.length > 20 && (
                <div className="pl-5">…and {verifyOutcome.issues.length - 20} more.</div>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 border-t border-ink-800 px-4 py-3">
          <button className="btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-ghost ml-auto"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
            title="Open an existing export and verify per-file SHA-256 checksums"
          >
            <FileCheck2 size={14} className="mr-1" />
            Verify archive
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip,application/zip"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void verifyPicked(f);
              e.target.value = '';
            }}
          />
          <button className="btn-primary" onClick={run} disabled={busy || !vaultKey}>
            <Download size={14} className="mr-1" />
            {busy ? 'Working…' : 'Export'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Drain the paginated `/api/attachments` endpoint. Older vaults return
 * everything in one call (nextCursor is null/undefined from legacy servers);
 * newer deployments cap at MAX_LIST_LIMIT and require cursor iteration.
 * Passing `limit: 1000` opts into pagination so we never implicitly rely on
 * the legacy "one giant response" path.
 *
 * Fails loud on two pagination hazards:
 *  - Cap exceeded: if we finish `MAX_PAGES` rounds and the server still
 *    hands back a `nextCursor`, we'd truncate the archive. Throw so the
 *    export aborts rather than silently producing an incomplete recovery
 *    ZIP. The cap is set to cover 50 000 attachments; beyond that the
 *    operator should raise this constant AND reconsider whether a single
 *    ZIP is still the right distribution format.
 *  - Cursor not advancing: server ids are monotonically increasing, so
 *    `nextCursor` must be strictly greater than the cursor we sent in.
 *    A repeated or regressing cursor would loop until we hit the cap;
 *    surfacing it as an explicit error catches a broken server-side
 *    paginator the moment it happens.
 */
async function collectAllAttachments() {
  const pageSize = 1000;
  const MAX_PAGES = 50;
  const all: Awaited<ReturnType<typeof api.listAttachments>>['attachments'] = [];
  let cursor: string | null | undefined = undefined;
  for (let pages = 0; pages < MAX_PAGES; pages++) {
    const res = await api.listAttachments({ limit: pageSize, cursor: cursor ?? undefined });
    all.push(...res.attachments);
    if (!res.nextCursor) return all;
    if (cursor != null && res.nextCursor <= cursor) {
      throw new Error(
        `attachment pagination cursor did not advance (${cursor} → ${res.nextCursor}); aborting export rather than loop`,
      );
    }
    cursor = res.nextCursor;
  }
  throw new Error(
    `attachment pagination exceeded ${MAX_PAGES} pages (> ${MAX_PAGES * pageSize} items); raise MAX_PAGES in ExportVaultModal.tsx if this vault legitimately holds that many attachments`,
  );
}

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  // WebCrypto's BufferSource overload is stricter under TS 5.7+ about the
  // inner buffer being ArrayBuffer (not SharedArrayBuffer). A plain cast
  // through `unknown` mirrors the `bs()` helper used in core/crypto.
  const buf = await crypto.subtle.digest('SHA-256', bytes as unknown as BufferSource);
  const arr = new Uint8Array(buf);
  let hex = '';
  for (const b of arr) hex += b.toString(16).padStart(2, '0');
  return hex;
}

function markdownFor(
  doc: DocumentPlain,
  attachmentFilenameById: Map<string, string>,
): string {
  const header = `# ${doc.title || '(untitled)'}\n\n`;
  const tags = doc.tags?.length ? `Tags: ${doc.tags.join(', ')}\n\n` : '';
  let body = doc.markdown || '';
  body = body.replace(
    /<inline-image\s*([^>]*)><\/inline-image>/gi,
    (_m, attrs) => {
      const idMatch = /attachmentId\s*=\s*"([^"]*)"/i.exec(attrs as string);
      const altMatch = /alt\s*=\s*"([^"]*)"/i.exec(attrs as string);
      const id = idMatch ? (idMatch[1] ?? '') : '';
      const alt = altMatch ? (altMatch[1] ?? '') : 'image';
      const fn = attachmentFilenameById.get(id);
      return fn ? `![${alt}](attachments/${fn})` : `![${alt}](#missing-attachment)`;
    },
  );
  body = body.replace(
    /<attachment-chip\s*([^>]*)><\/attachment-chip>/gi,
    (_m, attrs) => {
      const idMatch = /attachmentId\s*=\s*"([^"]*)"/i.exec(attrs as string);
      const nameMatch = /filename\s*=\s*"([^"]*)"/i.exec(attrs as string);
      const id = idMatch ? (idMatch[1] ?? '') : '';
      const name = nameMatch ? (nameMatch[1] ?? '') : 'attachment';
      const fn = attachmentFilenameById.get(id) ?? name;
      return `[📎 ${name}](attachments/${fn})`;
    },
  );
  return header + tags + body;
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

function uniqueSlug(base: string, used: Set<string>): string {
  const root = slugify(base) || 'untitled';
  if (!used.has(root)) return root;
  let i = 2;
  while (used.has(`${root}-${i}`)) i++;
  return `${root}-${i}`;
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
